import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useGlobalConfig } from 'src/hooks/useGlobalConfig';

const PromoHeader = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  
  const { 
    promoMessage, 
    promoMessageKeyWord, 
    promoMessageKeyCode, 
    promoInsertAfterWord 
  } = useGlobalConfig();

  useEffect(() => {
    if (promoMessage) {
        const dynamicStorageKey = `promoBannerDismissed_${promoMessageKeyCode || 'general'}`;
        const isDismissed = localStorage.getItem(dynamicStorageKey);

        if (!isDismissed) {
          const timer = setTimeout(() => setIsVisible(true), 800);
          return () => clearTimeout(timer);
        }
    }
  }, [promoMessage, promoMessageKeyCode]);

  const handleClose = (e) => {
    e.stopPropagation();
    setIsVisible(false);
    const dynamicStorageKey = `promoBannerDismissed_${promoMessageKeyCode || 'general'}`;
    localStorage.setItem(dynamicStorageKey, 'true');
  };

  const handleClick = () => {
    navigate('/membership'); 
  };

  const renderMessage = () => {
    if (!promoMessage) return null;

    let fullText = promoMessage;

    if (promoInsertAfterWord && promoMessageKeyWord && !fullText.includes(promoMessageKeyWord)) {
        fullText = fullText.replace(promoInsertAfterWord, `${promoInsertAfterWord} ${promoMessageKeyWord}`);
    }

    if (promoMessageKeyCode && !fullText.includes(promoMessageKeyCode)) {
        fullText = `${fullText} ${promoMessageKeyCode}`;
    }

    let parts = [fullText];
    
    if (promoMessageKeyCode) {
      parts = fullText.split(promoMessageKeyCode).reduce((acc, part, i, arr) => {
        if (i < arr.length - 1) {
          return [...acc, part, { type: 'code', text: promoMessageKeyCode }];
        }
        return [...acc, part];
      }, []);
    }

    if (promoMessageKeyWord) {
      parts = parts.flatMap(part => {
        if (typeof part === 'string') {
          return part.split(promoMessageKeyWord).reduce((acc, subPart, i, arr) => {
            if (i < arr.length - 1) {
              return [...acc, subPart, { type: 'keyword', text: promoMessageKeyWord }];
            }
            return [...acc, subPart];
          }, []);
        }
        return part;
      });
    }

    return parts.map((part, index) => {
      if (typeof part === 'string') return part;
      
      if (part.type === 'keyword') {
        return (
          <span key={index} style={{ color: '#5d87ff' }}>
            {part.text}
          </span>
        );
      }
      
      if (part.type === 'code') {
        return (
          <span
            key={index}
            style={{
              fontFamily: 'monospace',
              background: 'rgba(255,255,255,0.15)',
              padding: '2px 6px',
              borderRadius: '4px',
              whiteSpace: 'nowrap',
            }}
          >
            {part.text}
          </span>
        );
      }
      return null;
    });
  };

  if (!isVisible || !promoMessage) return null;

  return (
    <Slide direction="down" in={isVisible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 24, 
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1400, 
          pointerEvents: 'none', 
        }}
        className="promo-banner"
      >
        <Box
          onClick={handleClick}
          sx={{
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
            pl: 2.5,
            pr: 1,
            borderRadius: '100px', 
            background: 'rgba(49, 49, 60, 0.9)', 
            color: '#fff',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)', 
            border: '1px solid rgba(255,255,255,0.1)',
            backdropFilter: 'blur(12px)',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
            maxWidth: '90%',
            width: { xs: '75%', sm: 'auto' },
            
            '&:hover': {
              transform: 'scale(1.02) translateY(2px)',
              background: 'rgba(38, 38, 46, 1)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }
          }}
        >
          <Typography 
            sx={{ 
              fontSize: '1.2rem',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.2)' },
                '100%': { transform: 'scale(1)' },
              }
            }}
          >
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'flex-start' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                color: '#fff',
                textAlign: { xs: 'center', sm: 'left' },
                lineHeight: { xs: 1.5, sm: 'normal' },
                px: { xs: 1, sm: 0 },
              }}
            >
              {renderMessage()}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: 'rgba(255,255,255,0.4)',
              padding: 0.5,
              '&:hover': {
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Box>
      </Box>
    </Slide>
  );
};

export default PromoHeader;